import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Policies And Procedures
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Policies And Procedures</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This section of the documentation outlines the foundational policies and procedures necessary for ensuring
            that our SaaS medical application complies with the Health Insurance Portability and Accountability Act
            (HIPAA). Adhering to these guidelines safeguards the privacy and security of Protected Health Information
            (PHI) and supports our commitment to maintaining the trust of our users and patients. Detailed within are
            comprehensive descriptions of the privacy and security measures we have instituted, alongside protocols for
            managing and training our workforce in HIPAA compliance.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Privacy Policies:</b> These documents detail the mechanisms we have in place to protect patient
                  privacy, including how PHI is used and disclosed. Our Notice of Privacy Practices is also included,
                  ensuring patients are informed of their rights and our privacy practices.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Security Policies:</b> This section describes our strategies for protecting the electronic systems
                  and data from unauthorized access or breaches. It encompasses our risk management policies, sanctions
                  for security violations, and procedures for reviewing information system activities.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Employee Training and Management:</b> Recognizing that our workforce plays a critical role in
                  maintaining HIPAA compliance, this subsection covers our training materials, processes for managing
                  access to PHI, and the disciplinary measures for policy violations.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Objectives
          </Typography>
          <List>
            <ListItem>
              To provide clear and accessible documentation of our privacy and security policies in compliance with
              HIPAA regulations.
            </ListItem>
            <ListItem>
              To establish a framework for training and managing our workforce in practices that protect patient privacy
              and secure PHI.
            </ListItem>
            <ListItem>
              To demonstrate our ongoing commitment to upholding the highest standards of privacy and security in
              healthcare.
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Importance
          </Typography>
          <Typography variant="body1" paragraph>
            The policies and procedures documented in this section are not just regulatory requirements; they are
            integral to our ethical responsibility to the individuals whose information we manage. By rigorously
            adhering to these guidelines, we ensure the integrity and confidentiality of PHI, foster patient trust, and
            uphold our reputation as a secure and reliable healthcare SaaS provider.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Next Steps
          </Typography>
          <Typography variant="body1" paragraph>
            Following the overview of our policies and procedures, we encourage all staff and stakeholders to
            familiarize themselves with the detailed documentation provided in this section. Regular training and
            updates will be provided to ensure that all team members are informed of their roles and responsibilities in
            maintaining HIPAA compliance.
          </Typography>
          <Typography variant="body1" paragraph>
            For detailed information on specific policies or procedures, please refer to the corresponding subsections
            of this documentation. Your adherence and commitment to these guidelines are essential to our collective
            success in providing secure and compliant healthcare services.
          </Typography>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
